import type { DriverLicenceRow } from 'types/api.generated';
import {
  LICENCE_STATUS_BADGE,
  LICENCE_STATUS_LABEL,
  formatLicenceExpiry,
  formatVehicleType,
  getAdminLicenceStatusCopy,
  getDriverLicenceStatusCopy,
} from 'features/drivers/lib/driverLicenceStatus';

type LicenceStatusGridProps = {
  licences: DriverLicenceRow[];
  variant?: 'driver' | 'admin' | 'orgView';
  onSubmit?: (row: DriverLicenceRow) => void;
  onReview?: (row: DriverLicenceRow) => void;
};

export default function LicenceStatusGrid({
  licences,
  variant = 'driver',
  onSubmit,
  onReview,
}: LicenceStatusGridProps) {
  return (
    <ul className="bg-white rounded-2xl border border-slate-100 divide-y">
      {licences.map((row) => (
        <li key={row.vehicleType} className="px-4 py-4 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-slate-800">
              {formatVehicleType(row.vehicleType)}
            </span>
            <span
              className={`text-xs font-bold uppercase px-2.5 py-1 rounded-full shrink-0 ${
                LICENCE_STATUS_BADGE[row.status]
              }`}
            >
              {LICENCE_STATUS_LABEL[row.status]}
            </span>
          </div>
          <p className="text-sm text-slate-600">
            {variant === 'admin'
              ? getAdminLicenceStatusCopy(row)
              : variant === 'orgView'
                ? getAdminLicenceStatusCopy(row)
                : getDriverLicenceStatusCopy(row)}
          </p>
          {row.document && (
            <div className="text-xs text-slate-500 space-y-1">
              <a
                href={row.document.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary"
              >
                View document
              </a>
              <p>
                Expires {formatLicenceExpiry(row.document.expiresAt)}
                {row.document.isExpired && (
                  <span className="ml-2 text-orange-700 font-bold">Expired</span>
                )}
              </p>
            </div>
          )}
          {row.canGoOnline && variant === 'driver' && (
            <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
              You can go online with this type once you have a verified vehicle and
              start a session.
            </p>
          )}
          {variant === 'driver' && (row.canSubmit || row.canResubmit) && onSubmit && (
            <button
              type="button"
              onClick={() => onSubmit(row)}
              className="text-sm font-bold text-primary"
            >
              {row.canResubmit ? 'Submit again' : 'Submit licence'}
            </button>
          )}
          {variant === 'admin' && row.status === 'pending' && onReview && (
            <button
              type="button"
              onClick={() => onReview(row)}
              className="text-sm font-bold text-primary"
            >
              Review
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
